import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import '@progress/kendo-theme-default/dist/all.css';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoaderOverlay from "../../components/custom-loader/LoaderOverlay";
import { useUserContext } from '../../context/UserContext';
import AutoDapLogo from "../../images/logo/autodap.png";
import { decryptData, encryptData } from "../../services/dashboard-service/encryptionService";
import { resetUserPassword, verifyResetToken } from '../../services/dashboard-service/userService';
import History from "../../@history";


const initialErrorState = {
    NewPassword: { hasError: false, message: "" },
    RepeatNewPassword: { hasError: false, message: "" },
}

const ResetPassword = () => {
    const navigate = useNavigate()
    const { token } = useParams();
    const { setUser } = useUserContext()
    const [loading, setLoading] = useState(false)
    const [isErrorFound, setIsErrorFound] = useState(false)
    let [userData, setUserData] = useState([])
    const [errors, setErrors] = useState(initialErrorState);
    const [isValid, setIsValid] = useState(false)
    let [formData, setFormData] = useState({
        NewPassword: "",
        RepeatNewPassword: "",
    })
    const [showPassword, setShowPassword] = useState({
        NewPassword: false,
        RepeatNewPassword: false,
    });


    const getUserData = async () => {
        const tokenData = localStorage.getItem("userData");
        if (tokenData) {
            try {
                // Decrypt the token data
                const decryptedData = decryptData(tokenData);

                if (decryptedData) {
                    const parsedData = JSON.parse(decryptedData);

                    // Check if parsedData is valid
                    if (parsedData) {
                        return parsedData; // Return the user data if valid
                    } else {
                        throw new Error('Invalid data format');
                    }
                } else {
                    throw new Error('Failed to decrypt data');
                }
            } catch (error) {
                // Log the error, clear localStorage and redirect to login
                console.error("Error parsing user data:", error);
                localStorage.removeItem('userData');
                navigate('/login')
                History.push('/login');
                return null;
            }
        } else {
            navigate('/login')
            History.push('/login');
            return null;
        }
    }

    useEffect(() => {
        let fetchUserData = async () => {
            setLoading(true)
            let data = await getUserData()
            if (data) {
                setUserData(data);
            }
            setLoading(false)
        }
        fetchUserData()
    }, [])

    useEffect(() => {
        if (userData && userData.SecretKey) { // Check if userData and SecretKey are available
            setLoading(true);
            const apikey = userData.SecretKey;

            const verifyToken = async () => {
                try {
                    if (token && apikey) {
                        const response = await verifyResetToken(token, apikey); // Assuming verifyResetToken is a function that verifies the token
                        if (response && response.status === 200) {
                            setIsErrorFound(false);
                            setIsValid(true);
                        } else if (response?.error?.statusCode === 401 || response?.status === 401) { 
                            setIsValid(false);
                            setIsErrorFound(true);
                        } else {
                            setIsErrorFound(false);
                            setIsValid(false);
                        }
                    }
                } catch (error) {
                    setIsErrorFound(true);
                    setIsValid(false);
                } finally {
                    setLoading(false);
                }
            };
            verifyToken();
        }
    }, [userData, token]);


    function handleClickForLogin() {
        navigate('/login');
    }

    if (isErrorFound && !loading) {
        return (
            <div style={{ display: "flex", alignItems: 'center', justifyContent: 'center', height: "100vh", width: "100%", flexDirection: "column" }}>
                <div className="no-data-found">
                    <div className="no-data-icon">
                        <FontAwesomeIcon icon={faCircleExclamation} size="7x" />
                    </div>
                </div>
                <Typography variant="h6" color="error" gutterBottom style={{ marginTop: "1rem" }}>
                    Token is either invalid or expired. Please Login again.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClickForLogin}
                    sx={{ mt: 2 }}>
                    Go to Login
                </Button>
            </div>
        )
    }





    const handleChange = (e) => {
        const { name, value } = e.target
        const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*()\-_\[\]{}]).{6,}$/g;
        const regexMessage =
            "Password must be at least 6 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character.";

        if (!value.trim()) {
            setErrors((prev) => ({ ...prev, [name]: { hasError: true, message: `${name} is Required` } }))
        }
        else if (!regex.test(value)) {
            setErrors((prev) => ({ ...prev, [name]: { hasError: true, message: regexMessage } }));
        }
        else {
            setErrors((prev) => ({ ...prev, [name]: { hasError: false, message: "" } }))
        }
        setFormData({ ...formData, [name]: value })
    }



    const togglePasswordVisibility = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field]
        }))
    }


    const handleCancel = () => {
        setFormData({
            NewPassword: "",
            RepeatNewPassword: "",
        })
        setShowPassword({
            NewPassword: false,
            RepeatNewPassword: false,
        });
        setErrors(initialErrorState)
    }


    function updateFieldError(fieldName, message, toastMessage) {
        setErrors((prev) => ({
            ...prev,
            [fieldName]: { hasError: true, message },
        }));
        toast(toastMessage, { autoClose: true, type: "error" });
    }


    function validatePasswordForm() {
        let hasError = false;

        // Check if any password field is empty
        if (
            !formData.NewPassword.trim() ||
            !formData.RepeatNewPassword.trim()
        ) {
            setErrors((prev) => ({
                ...prev,
                NewPassword: {
                    hasError: !formData.NewPassword.trim(),
                    message: "New Password is required.",
                },
                RepeatNewPassword: {
                    hasError: !formData.RepeatNewPassword.trim(),
                    message: "Please repeat the new password.",
                },
            }));
            toast("All password fields are required.", {
                autoClose: true,
                type: "error",
            });
            hasError = true;
        }
        const passwordRegex =
            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*()\-_\[\]{}]).{6,}$/;
        if (!passwordRegex.test(formData.NewPassword)) {
            updateFieldError("NewPassword",
                "New Password must include at least 6 characters, one uppercase letter, one lowercase letter, one number, and one special character.",
                "Invalid New Password."
            );
            hasError = true;
        }
        else if (formData.NewPassword !== formData.RepeatNewPassword) {
            updateFieldError("RepeatNewPassword", "New Password and Repeat Password Does Not Match.", "Invalid Repeat Password.")
            hasError = true;
        }
        else {
            hasError = false
        }
        return hasError;
    }

    function updateFieldError(fieldName, message, toastMessage) {
        setErrors((prev) => ({
            ...prev,
            [fieldName]: { hasError: true, message },
        }));
        toast(toastMessage, { autoClose: true, type: "error" });
    }

    const updateUserProfileHandler = async (e) => {
        e.preventDefault()
        const hasErrors = validatePasswordForm();
        if (hasErrors) {
            return;
        }
        try {
            let apikey = userData?.SecretKey
            setLoading(true)
            let updatedProfileResponse = await resetUserPassword(apikey, formData, userData.Token)
            if (updatedProfileResponse && updatedProfileResponse.status === 200) {
                if (userData) {
                    let userDataNew = { ...userData, Is_FirstPasswordChanged: 1 }
                    localStorage.setItem("userData", encryptData(JSON.stringify(userDataNew)))
                    toast("Password Update Successfully.", {
                        autoClose: true,
                        type: "success"
                    })
                    handleCancel()
                    setLoading(false)
                    let homeRoute = userData?.routes[0]?.route ? userData?.routes[0]?.route : "/"
                    navigate(homeRoute)
                    return;
                }
                else {
                    navigate("/login")
                    setLoading(false)
                    return;
                }
            }
            else {
                toast(updatedProfileResponse?.message, {
                    autoClose: true,
                    type: "error"
                })
                setLoading(false);
                return;

            }
        } catch (error) {
            toast("Try Again Later.", {
                autoClose: true,
                type: "error"
            })
            setLoading(false)
            return;
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="login-card-container">
            <LoaderOverlay loading={loading} />
            <Card className="custom-login-card" style={{ maxWidth: "600px", padding: "20px" }}>
                <CardHeader style={{ width: '100%', display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={AutoDapLogo} alt='autodap' style={{ height: "100%", width: "280px" }} />
                </CardHeader>
                <Typography variant="h5" gutterBottom style={{ fontWeight: "bold", color: "#333", marginTop: "1rem", paddingLeft: "16px" }}>
                    Reset Password
                </Typography>
                <form onSubmit={updateUserProfileHandler} className="login-form">
                    <CardBody className="login-form" style={{ paddingTop: "0px" }}>
                        <Box
                            component="div"
                            noValidate
                            autoComplete="off"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                marginTop: 2,
                            }}
                        >
                            <TextField
                                name="NewPassword"
                                label="New Password"
                                variant="outlined"
                                type={showPassword.NewPassword ? "text" : "password"}
                                error={errors.NewPassword?.hasError}
                                helperText={errors.NewPassword?.message || ''}
                                onChange={handleChange}
                                value={formData?.NewPassword}
                                required
                                sx={{ background: "#ffffff" }}
                                FormHelperTextProps={{
                                    sx: {
                                        width: "100%",
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => togglePasswordVisibility('NewPassword')}
                                                edge="end"
                                            >
                                                {showPassword.NewPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}

                            />

                            <TextField
                                name="RepeatNewPassword"
                                label="Repeat New Password"
                                variant="outlined"
                                type={showPassword.RepeatNewPassword ? "text" : "password"}
                                fullWidth
                                error={errors.RepeatNewPassword?.hasError}
                                helperText={errors.RepeatNewPassword?.message || ''}
                                onChange={handleChange}
                                value={formData?.RepeatNewPassword}
                                required
                                sx={{ background: "#ffffff" }}
                                FormHelperTextProps={{
                                    sx: {
                                        width: "100%",
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => togglePasswordVisibility('RepeatNewPassword')}
                                                edge="end"
                                            >
                                                {showPassword.RepeatNewPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: "1rem", justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type='submit'
                                    style={{ marginTop: "1rem", alignSelf: "flex-end" }}
                                >
                                    Save Password
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleCancel}
                                    style={{ marginTop: "1rem", alignSelf: "flex-end", backgroundColor: "#f5f5f5", color: "rgba(0, 0, 0, 0.87)" }}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>
                    </CardBody>
                </form>
            </Card>
        </div>
    )
}

export default ResetPassword